import React from 'react';
import { cn } from '@/lib/cn';

type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'transparent';
type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  fullWidth?: boolean;
  /** Optional icon rendered before the label */
  iconLeft?: React.ReactNode;
  /** Optional icon rendered after the label */
  iconRight?: React.ReactNode;
}

// Simple CSS spinner component
function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cn('animate-spin', className)}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

export function Button({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  fullWidth = false,
  className,
  children,
  iconLeft,
  iconRight,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      className={cn(
        // Base styles
        'relative inline-flex items-center justify-center rounded-full font-semibold transition-colors gap-2',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-accent',
        // Disabled: force text + icons to tertiary
        'disabled:opacity-60 disabled:text-tertiary',
        'text-body-2-high',
        'cursor-pointer disabled:!cursor-default disabled:bg-secondary',
        // Variant styles
        variant === 'primary' && 'text-inverted bg-accent hover:bg-accent-hover',
        variant === 'secondary' && 'text-accent bg-secondary hover:bg-secondary-hover',
        variant === 'tertiary' &&
          'text-accent bg-primary hover:bg-primary-hover active:bg-primary-press disabled:!bg-primary',
        variant === 'transparent' &&
          'text-accent bg-trans hover:bg-transparent-hover active:bg-transparent-press shadow',
        // Size styles
        size === 'md' && 'px-6 py-4 text-sm leading-5',
        size === 'sm' && 'px-4 py-3 text-sm leading-5',
        // Full width
        fullWidth && 'w-full',
        className,
      )}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <span className="inline-flex">
          <Spinner className={cn(size === 'sm' ? 'h-3 w-3 icon-accent' : 'h-4 w-4 icon-accent')} />
        </span>
      ) : null}
      {iconLeft ? <span className="inline-flex">{iconLeft}</span> : null}
      {children}
      {iconRight ? <span className="inline-flex">{iconRight}</span> : null}
    </button>
  );
}
